import {useState, useEffect, useCallback} from 'react';

import {contentCreatingRoleEnum, trainingAndFieldRoleEnum, workspaceUsersEnum} from '../../../serverClientShared/sharedEnums';
import {generalStore, modalStore} from '../../../index';

const handleSingleSelect = (currentSet, option, noneValue) => {
    if (currentSet.has(option)) return new Set([noneValue]);
    return new Set([option]);
};

const handleMultiSelect = (currentSet, option, noneValue, allValues, isFilterMode) => {
    let newSet = new Set(currentSet);
    if (isFilterMode) {
        newSet.has(option) ? newSet.delete(option) : newSet.add(option);
        return newSet;
    }
    if (newSet.has(noneValue) && newSet.size === 1 && option === noneValue) {
        newSet = new Set(allValues);
        newSet.delete(noneValue);
    } else if (option === noneValue) {
        newSet = new Set([noneValue]);
    } else if (newSet.has(option)) {
        newSet.delete(option);
        if (newSet.size === 0) newSet.add(noneValue);
    } else {
        newSet.add(option);
        newSet.delete(noneValue);
    }
    return newSet;
};

const ROLE_HANDLERS = {
    trainingAndField: [trainingAndFieldRoleEnum.NONE, Object.values(trainingAndFieldRoleEnum)],
    contentCreating: [contentCreatingRoleEnum.NONE, Object.values(contentCreatingRoleEnum)],
    administration: [workspaceUsersEnum.NONE, Object.values(workspaceUsersEnum)],
};

export const getRoleDisplayText = (selected, allCount, noneValue, isFilterMode) => {
    if (selected.size === 0) return '-';
    if (selected.size === 1) {
        const value = Array.from(selected)[0];
        if (value === noneValue) return isFilterMode ? generalStore.translate('none') : 'None';
        if (value === trainingAndFieldRoleEnum.OPERATOR_3D) return 'Operator MR';
        return generalStore.translate(value);
    }
    if (selected.size === allCount) return generalStore.translate('AllRoles');
    return `${selected.size} ${generalStore.translate('roles', true)}`;
};

const useRoleSelection = ({isSingleSelect, isFilterMode, isOpen}) => {
    const [training, setTraining] = useState(new Set());
    const [content, setContent] = useState(new Set());
    const [admin, setAdmin] = useState(new Set());

    useEffect(() => {
        if (isOpen) {
            setTraining(modalStore.chooseInitRoleState?.trainingAndField ?? new Set(['none']));
            setContent(modalStore.chooseInitRoleState?.contentCreating ?? new Set(['none']));
            setAdmin(modalStore.chooseInitRoleState?.administration ?? new Set(['none']));
        }
    }, [isOpen]);

    const getSelection = useCallback((type) => {
        const map = {trainingAndField: training, contentCreating: content, administration: admin};
        return map[type] || new Set();
    }, [training, content, admin]);

    const setSelection = useCallback((type, option) => {
        const setters = {trainingAndField: setTraining, contentCreating: setContent, administration: setAdmin};
        const setter = setters[type];
        const [noneVal, allVals] = ROLE_HANDLERS[type];
        const current = getSelection(type);

        const isAdminNonFilter = type === 'administration' && !isFilterMode;
        const newSet = (isSingleSelect || isAdminNonFilter)
            ? handleSingleSelect(current, option, noneVal)
            : handleMultiSelect(current, option, noneVal, allVals, isFilterMode);

        setter(newSet);
        return isSingleSelect || isAdminNonFilter;
    }, [isSingleSelect, isFilterMode, getSelection]);

    return {training, content, admin, getSelection, setSelection, setTraining, setContent, setAdmin};
};

export default useRoleSelection;
